import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import { FullPageContent } from '@/components/layout/full-page-content';
import { LoadError } from '@/components/ui/load-error';
import { useCurrentAsset } from '@/hooks/use-current-asset';

import { GetLogCountQuery } from '../api/queries/get-log-count';
import { ListSystemLogs } from '../api/queries/list-system-logs';
import {
  SERVER_ERROR_MESSAGE,
  SYSTEM_DEPENDENCY_FAILED_ERROR_MESSAGE,
} from '../constants/error-messages';
import {
  FallbackBackendUnavailable,
  FallbackDependencyFailed,
} from '../view/components/system-logs-fallback';
import { SystemLogsFilters } from '../view/components/system-logs-filters';
import { SystemLogsPagination } from '../view/components/system-logs-pagination';
import { SystemLogsResultsBar } from '../view/components/system-logs-results-bar';
import { SystemLogsResultsBarLoading } from '../view/components/system-logs-results-bar-loading';
import { SystemLogsTable } from '../view/components/system-logs-table';
import { SystemLogsTableLoading } from '../view/components/system-logs-table-loading';
import { SystemLogsTableRefetchOverlay } from '../view/components/system-logs-table-refetch-overlay';
import { useSystemLogsQueryState } from '../view/state/use-system-logs-query';
/**
 * System Logs Page - works in two contexts:
 * 1. Portfolio view (/system-logs) - shows site filter, user selects site(s)
 * 2. Asset view (/asset/:siteId/system-logs) - hides site filter, uses siteId from URL
 */
export function SystemLogsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const asset = useCurrentAsset();
  const [filters] = useSystemLogsQueryState();

  const isAssetContext = Boolean(asset.asset);

  const { data: { count: totalCount } = { count: 0 } } =
    GetLogCountQuery.useQuery({
      siteAccureId: asset.accureId,
      filters,
    });

  const query = ListSystemLogs.useQuery({
    systemId: asset.accureId,
    filters,
    page,
    pageSize,
  });

  // Track previous filters to detect actual changes (not just reference changes)
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const curr = filters;

    // Deep comparison: only reset if actual values changed
    const hasChanged =
      JSON.stringify(prev.coreFilters) !== JSON.stringify(curr.coreFilters) ||
      JSON.stringify(prev.batteryFilters) !==
        JSON.stringify(curr.batteryFilters) ||
      JSON.stringify(prev.pcsFilters) !== JSON.stringify(curr.pcsFilters);

    if (hasChanged) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  // State machine: Loading → Error → Data/Empty
  // Also handles refetch states with overlay
  const renderContent = () => {
    // Initial load: show full skeleton
    if (query.isLoading) {
      return <SystemLogsTableLoading />;
    }

    if (query.isError) {
      Sentry.captureException(query.error);
      const errorMessage = String(query.error);

      if (errorMessage.includes(SYSTEM_DEPENDENCY_FAILED_ERROR_MESSAGE)) {
        return <FallbackDependencyFailed />;
      }

      if (errorMessage.includes(SERVER_ERROR_MESSAGE)) {
        return <FallbackBackendUnavailable />;
      }

      return <LoadError title="Failed to load system logs" />;
    }

    const table = query.data?.table;
    if (!table) {
      return <FallbackBackendUnavailable />;
    }

    // Refetch: show overlay on existing data
    const isRefetching = query.isFetching && !query.isLoading;

    return (
      <div className="relative flex flex-1 flex-col">
        <SystemLogsTable
          table={{
            columns: table.columns,
            rows: table.rows,
          }}
        />
        {isRefetching && <SystemLogsTableRefetchOverlay />}
      </div>
    );
  };

  // this is the count of results per page, total count is currently not available!
  const table = query.data?.table;

  return (
    <FullPageContent
      scrollable
      testId="system-logs-page"
      className="flex size-full flex-col gap-4 p-4"
    >
      <div className="flex shrink-0 items-center gap-2">
        <div className="flex-1">
          <SystemLogsFilters isAssetContext={isAssetContext} />
        </div>
      </div>
      {query.isLoading ? (
        <SystemLogsResultsBarLoading />
      ) : (
        <SystemLogsResultsBar total={totalCount} table={table} />
      )}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-auto"
        data-testid="system-logs-content"
      >
        {renderContent()}
      </div>
      {!query.isLoading && (
        <div className="mt-2 flex shrink-0 items-center justify-start">
          <SystemLogsPagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            disabled={query.isFetching}
            hasNextPage={!!query.data?.next}
            hasPreviousPage={!!query.data?.previous}
          />
        </div>
      )}
    </FullPageContent>
  );
}
